/**
 * Minimal client-side JWT payload decoding.
 *
 * We only base64-decode the payload to read claims — we do NOT verify the
 * signature (the server does that on every request). This is signature-agnostic,
 * so it works for both the cloud api-service token (HS256/RS256) and the desktop
 * Rust server token in standalone mode.
 *
 * The `permissions` claim is the canonical source of the current user's
 * permissions in BOTH modes (Rust `Claims.permissions` and Go `Claims.Permissions`
 * both serialize to `"permissions"`). owner/admin tokens carry an empty array by
 * design — they bypass enforcement — so gating relies on `org_role`, not the array.
 */

interface JwtPayload {
  permissions?: string[];
  org_role?: string;
  role_slug?: string;
  role?: string;
  exp?: number;
}

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64)) as JwtPayload;
  } catch {
    return null;
  }
}

/** Extract the permission strings embedded in a JWT, or `[]` if none/invalid. */
export function permissionsFromToken(token: string | null | undefined): string[] {
  if (!token) return [];
  const payload = decodeJwtPayload(token);
  return payload?.permissions ?? [];
}

/**
 * The staff role slug in a JWT, or "" if none/invalid.
 *
 * Read because the server bypasses enforcement on it as well as on org_role
 * (rbac.IsBypassRole): a role slug of owner or admin passes every check. Gating
 * the UI on org_role alone hid actions the server would have allowed.
 */
export function roleSlugFromToken(token: string | null | undefined): string {
  if (!token) return "";
  return decodeJwtPayload(token)?.role_slug ?? "";
}
